<script setup>
import { computed, ref, useAttrs, watch } from 'vue'

// Glyph per size (FDS Web Buttons §03): 24 on XL/L, 20 on M, 16 on S.
const ICON_SIZES = { xl: 24, l: 24, m: 20, s: 16 }

const VARIANT_CLASSES = {
    primary: 'base-button-primary',
    inverse: 'base-button-inverse',
    secondary: 'base-button-secondary',
    outline: 'base-button-outline',
    tertiary: 'base-button-tertiary',
    destructive: 'base-button-destructive',
    // DEPRECATED chips — today's look, kept until T10 Badges ships the tag component.
    tag: 'base-button-tag',
    tagSecondary: 'base-button-tag-secondary',
}

const props = defineProps({
    variant: {
        type: String,
        default: 'primary',
        // Keep in sync with VARIANT_CLASSES below.
        // NOTE: inlined (not a module-scope const) — defineProps() cannot
        // reference locally declared `<script setup>` variables, they are
        // hoisted outside of setup().
        validator: value =>
            ['primary', 'inverse', 'secondary', 'outline', 'tertiary', 'destructive', 'tag', 'tagSecondary'].includes(
                value
            ),
    },
    size: {
        type: String,
        default: 'l',
        // NOTE: inlined for the same reason as `variant` above.
        validator: value => ['xl', 'l', 'm', 's'].includes(value),
    },
    // Hug by default (T8); Fill is opt-in.
    fill: {
        type: Boolean,
        default: false,
    },
    // Circle, glyph only; `label` becomes the accessible name.
    iconOnly: {
        type: Boolean,
        default: false,
    },
    icon: {
        type: String,
        default: '',
    },
    trailingIcon: {
        type: String,
        default: '',
    },
    label: {
        type: String,
        default: '',
    },
    // Panel-open tint — the ONLY two-state case where the container changes.
    selected: {
        type: Boolean,
        default: false,
    },
    // Spinner in the icon slot, label held, width frozen, aria-disabled (never disabled).
    loading: {
        type: Boolean,
        default: false,
    },
    // On-state for chips and Follow (glyph/label change, container never does).
    active: {
        type: Boolean,
        default: false,
    },
})

const attrs = useAttrs()
const root = ref(null)
const frozenWidth = ref(null)

const isLabel = computed(() => !attrs.onClick && attrs.type !== 'submit')
const iconSize = computed(() => ICON_SIZES[props.size])

// Loading with no glyph: the spinner would land INSIDE the frozen width and ellipsise the
// label, so this class has the stylesheet take its room out of the padding instead
// (follow-up gap 2, decision D1). With a glyph, the spinner just takes that glyph's room (a
// trailing glyph hides while the spinner leads). Deprecated chips keep their own geometry.
const needsSpinnerRoom = computed(
    () =>
        props.loading &&
        !props.iconOnly &&
        !props.icon &&
        !props.trailingIcon &&
        !['tag', 'tagSecondary'].includes(props.variant)
)

const classes = computed(() => [
    'base-button',
    VARIANT_CLASSES[props.variant],
    `base-button-${props.size}`,
    props.fill && 'base-button-fill',
    props.iconOnly && 'base-button-icon-only',
    props.selected && 'base-button-selected',
    props.loading && 'base-button-loading',
    needsSpinnerRoom.value && 'base-button-loading-no-icon',
    props.active && 'base-button-active',
    isLabel.value && 'base-button-cannot-be-hovered',
])

// The used width, not the painted one (decision D2). `getBoundingClientRect()` includes the
// press transform — a click lands at mouseup while the 0.97 scale is still easing back — so
// it under-measured by ~3% and ellipsised the frozen label. The computed `width` is
// transform-free and, under the app-wide `border-box` reset (app.vue) the padding trim
// below also relies on, matches an inline `width` box for box. It serialises to 6
// significant digits while Blink truncates an inline width to a 1/64px grid, so it is
// rounded UP to that grid and is never short. Anything unresolvable (jsdom, `auto` under
// display: none) falls back to the rect.
function layoutWidth(element) {
    const width = parseFloat(window.getComputedStyle(element).width)
    return Number.isFinite(width) ? Math.ceil(width * 64) / 64 : element.getBoundingClientRect().width
}

// Measure BEFORE the spinner swaps in (flush: 'pre' runs ahead of the DOM patch),
// so the frozen width is the label's width, not the spinner's.
watch(
    () => props.loading,
    isLoading => {
        frozenWidth.value = isLoading && root.value ? layoutWidth(root.value) : null
    },
    { flush: 'pre' }
)

const style = computed(() => (frozenWidth.value ? { width: `${frozenWidth.value}px` } : undefined))

function onClickCapture(event) {
    if (!props.loading) return
    event.preventDefault()
    event.stopImmediatePropagation()
}
</script>

<template>
    <button
        ref="root"
        :class="classes"
        :style="style"
        :aria-label="iconOnly ? label : undefined"
        :aria-disabled="loading ? 'true' : undefined"
        :aria-busy="loading ? 'true' : undefined"
        @click.capture="onClickCapture"
    >
        <BaseIcon
            v-if="loading"
            class="base-button-icon spin"
            name="spinner"
            :size="iconSize"
        />
        <BaseIcon
            v-else-if="icon"
            class="base-button-icon"
            :name="icon"
            :size="iconSize"
        />
        <span
            v-if="!iconOnly"
            class="base-button-main"
        >
            <slot />
        </span>
        <BaseIcon
            v-if="trailingIcon && !iconOnly && !loading"
            class="base-button-icon base-button-icon-trailing"
            :name="trailingIcon"
            :size="iconSize"
        />
    </button>
</template>

<style lang="scss" scoped>
@use 'sass:list';
@use 'sass:map';
@use 'sass:math';
@use '@design' as *;

// Geometry per size (Buttons §01): height · horizontal padding · max width.
$sizes: (
    'xl': (56px, $space-32, 320px),
    'l': (48px, $space-24, 320px),
    'm': (40px, $space-16, 240px),
    's': (32px, $space-12, 180px),
);

// Flat hover/press pair shared by every filled variant.
// R1 review fix #4: `.base-button-active` must NEVER repaint a flat variant's
// container (T8 toggle taxonomy — only the glyph/label change for `active`);
// the old second arm here painted the pressed background whenever
// `.base-button-active` was present. The tag chip keeps its own
// `&.base-button-active::before` rule below — that's a separate, sanctioned
// deprecated-chip look, not this mixin.
@mixin flatStates($hover, $pressed) {
    &:not(:disabled):not(.base-button-loading):hover {
        background: $hover;
    }

    &:not(:disabled):not(.base-button-loading):active {
        background: $pressed;

        @include transition((background-color, transform), fast, exit);
    }
}

// Top-level selectors stay wrapped in `:where()` so the scoping attribute does
// not raise their specificity above the overrides callers pass in via `:class`.
// See "Component authoring standard > Styles" in the README.
:where(.base-button) {
    @include bodyEmphasis;

    position: relative;
    display: inline-flex;
    gap: $space-8;
    align-items: center;
    justify-content: center;
    min-width: 0;
    max-width: 100%;
    padding: 0;
    margin: 0;
    color: $c-text-primary;
    text-align: center;
    white-space: nowrap;
    cursor: pointer;
    background: none;
    border: 0;
    border-radius: $radius-full;

    @include transition((background-color, color, border-color, transform), base);

    // R2 review fix (ruling R13, finding #2): a bare `&:focus-visible` chained
    // straight off `:where()` stays NESTED INSIDE it (same mechanism as round
    // 1's disabled bug), so it's only specificity 0,1,0 — that TIES app.vue's
    // own global `:focus-visible` rule (resolved only by stylesheet load
    // order, not intent) and LOSES outright to Firefox's UA rules
    // `button:-moz-focusring { outline: 1px dotted ButtonText }` (0,1,1 — ties
    // on the class/pseudo count, then the type selector decides) and
    // `[type='button']:-moz-focusring` / `[type='submit']:-moz-focusring`
    // (same normalize.css rule, comma-grouped — 0,2,0, which a 0,2,0 fix would
    // only TIE, not beat; R16 finding #2). Wrapping the include in a real (if
    // redundant) `.base-button` compound pushes the scope id — and the whole
    // rule — outside `:where()`, raising it to 0,3,0, which beats all of the
    // above outright regardless of their type-selector count or load order.
    // The `focusRing` mixin itself (`_elevation.scss`) is left alone — other
    // components use it; this fix is local to BaseButton only.
    &.base-button {
        @include focusRing;
    }

    // Press: scale 0.97 at motion/fast. R1 review fix #3: the reduced-motion
    // override used to live as a separate rule at the bottom of this block with
    // a shorter selector (missing `:not(.base-button-loading)`) — one pseudo-class
    // less specificity than THIS rule, so it always lost regardless of source
    // order or the media query matching. Nesting `@media` inside the exact same
    // selector guarantees identical specificity, so the later (media-guarded)
    // declaration wins the tie whenever reduced motion is on.
    &:not(:disabled):not(.base-button-loading):active {
        transform: scale(0.97);

        @media (prefers-reduced-motion: reduce) {
            transform: none;
        }
    }

    // R3 review fix (ruling R16, finding #1b/#1c): the deprecated chips keep
    // today's look (plan decision D3) and must NOT pick up FDS pill geometry —
    // the exclusion keeps `max-width`/`height`/`padding` (and, below,
    // `min-width`) off `.base-button-tag`/`.base-button-tag-secondary` even
    // though a chip carries a size class too (they're passed `size="s"` at
    // every current call site). Chips set their own `height`/`padding`
    // directly in their own blocks below regardless, but nothing there
    // overrides `max-width` — left unexcluded, a chip at size S/M would cap at
    // 180px/240px and truncate a long genre name.

    // Final review fix (ruling R19, C1): the exclusion — and the icon-only
    // compound — live inside `:where()` so they add ZERO specificity. As bare
    // `:not(…):not(…)` they lifted every size rule to 0,4,0 (icon-only 0,5,0),
    // which out-ranked `fill`'s `max-width: none` and the XL→L fallback below
    // (both 0,2,0) and the consumer `.thing[class]` override recipe (0,3,0).
    // Every geometry rule here is 0,2,0; later rules win their ties by source
    // order, and any 0,3,0 consumer rule wins outright.
    @each $name, $geometry in $sizes {
        &.base-button-#{$name}:where(:not(.base-button-tag, .base-button-tag-secondary)) {
            max-width: list.nth($geometry, 3);
            height: list.nth($geometry, 1);
            padding: 0 list.nth($geometry, 2);

            &:where(.base-button-icon-only) {
                width: list.nth($geometry, 1);
                padding: 0;
            }
        }
    }

    // XL is desktop-only: below the desktop tier it renders as L. Same 0,2,0
    // as the size loop above and later in source, so it wins the tie.
    &.base-button-xl {
        @media (max-width: ($desktop - 0.02px)) {
            height: 48px;
            padding: 0 $space-24;

            // `padding: 0` again — the fallback's own `padding: 0 24px` just
            // above ties the loop's icon-only reset and wins it on source order.
            &:where(.base-button-icon-only) {
                width: 48px;
                padding: 0;
            }
        }
    }

    // Loading with no glyph (follow-up gap 2, decision D1): the watch in <script> freezes
    // the width, so the spinner's room (glyph + gap) comes out of the horizontal padding,
    // not the label. Figma's Loading variants (347:28 …) are drawn 32px wider than Default;
    // the ticket's frozen-width lock wins.
    // Per size: (spinner-side left, label-side right, gap). The label side keeps at least 4px
    // because the label's last glyph, unlike the round spinner, runs into the pill's curve and
    // the outline ring when trimmed tight (M and S did with a symmetric trim), so M and S
    // narrow the gap instead and S is asymmetric. S keeps 2px on the spinner side (at 0
    // its ink sat 0.4px from the outline ring). Width held exactly: left + right + glyph +
    // gap = 2 × the size's padding (XL 16+16+24+8 = 64 · L 8+8+24+8 = 48 · M 4+4+20+4 = 32 ·
    // S 2+4+16+2 = 24).
    // Same 0,2,0 as the size loop and later in source, so it wins the tie (`:where()` keeps
    // the size class specificity-free). Chips never get the class (see `needsSpinnerRoom`).
    // A consumer `.x[class] { padding }` override (0,3,0) wins outright and must handle
    // loading itself.
    $loading-geometry: (
        'xl': ($space-16, $space-16, $space-8),
        'l': ($space-8, $space-8, $space-8),
        'm': ($space-4, $space-4, $space-4),
        's': (math.div($space-4, 2), $space-4, math.div($space-4, 2)),
    );

    @each $name, $geometry in $loading-geometry {
        &.base-button-loading-no-icon:where(.base-button-#{$name}) {
            gap: list.nth($geometry, 3);
            padding: 0 list.nth($geometry, 2) 0 list.nth($geometry, 1);
        }
    }

    // XL renders as L below desktop, so it takes L's loading geometry. Later than the
    // XL→L fallback above, so it wins that tie too.
    &.base-button-loading-no-icon:where(.base-button-xl) {
        $l-geometry: map.get($loading-geometry, 'l');

        @media (max-width: ($desktop - 0.02px)) {
            gap: list.nth($l-geometry, 3);
            padding: 0 list.nth($l-geometry, 2) 0 list.nth($l-geometry, 1);
        }
    }

    // See the R16/R19 comments above the size loop — chips must not get this
    // either, and the exclusion stays specificity-free.
    &.base-button-xl:where(:not(.base-button-tag, .base-button-tag-secondary)),
    &.base-button-l:where(:not(.base-button-tag, .base-button-tag-secondary)) {
        min-width: 88px;

        &:where(.base-button-icon-only) {
            min-width: 0;
        }
    }

    // R2 review fix (ruling R13, finding #1): normalize.css's
    // `button, input, … { font-size: 100%; line-height: 1.15 }` is specificity
    // 0,0,1 and beats the root's `@include bodyEmphasis` above (0,0,0) for
    // exactly these two properties — XL/L labels were inheriting the parent's
    // font size and a 1.15 line-height instead of Body Emphasis' 16/24.
    // `font-weight`/`letter-spacing`/`color` aren't touched by normalize for
    // `<button>`, so they're correctly left alone at the root — leaving
    // `color` there (rather than duplicating it here too) is what keeps every
    // variant's own `color` rule winning the source-order tie below,
    // unchanged from round 1. The (specificity-free, R19) exclusion keeps the
    // two deprecated chips off this rule: they carry a
    // `.base-button-l`/`.base-button-xl` size class by default too, but must
    // stay at their own 12px/700 with nothing extra leaking in. At 0,2,0 this
    // still beats normalize's 0,0,1 outright.
    &.base-button-xl:where(:not(.base-button-tag, .base-button-tag-secondary)),
    &.base-button-l:where(:not(.base-button-tag, .base-button-tag-secondary)) {
        font-size: 16px;
        line-height: 24px;
    }

    &.base-button-m {
        @include bodySmallEmphasis;
    }

    &.base-button-s {
        @include secondary;

        font-weight: 600;
        color: $c-text-primary;
    }

    &.base-button-fill {
        display: flex;
        width: 100%;
        max-width: none;
    }

    // M and S sit under the 48px touch floor: an invisible hit expander.
    // R3 review fix (ruling R16, finding #1a): excludes the deprecated chips —
    // in a wrapping genre-pill row (chips are passed size="s"), the expander
    // overlapped the chip on the next line and stole its clicks. (R19: the
    // exclusion is specificity-free, same as the size loop.)
    &.base-button-m:where(:not(.base-button-tag, .base-button-tag-secondary))::after,
    &.base-button-s:where(:not(.base-button-tag, .base-button-tag-secondary))::after {
        position: absolute;
        top: 50%;
        left: 50%;
        width: max(100%, 48px);
        height: max(100%, 48px);
        content: '';
        transform: translate(-50%, -50%);
    }

    // ---- variants -------------------------------------------------------

    &.base-button-primary {
        color: $c-text-on-accent;
        background: $c-accent-default;

        @include flatStates($c-accent-hover, $c-accent-pressed);
    }

    &.base-button-inverse {
        color: $c-text-on-inverse;
        background: $c-surface-inverse;

        @include flatStates(
            linear-gradient($c-state-hover-on-light, $c-state-hover-on-light) $c-surface-inverse,
            linear-gradient($c-state-pressed-on-light, $c-state-pressed-on-light) $c-surface-inverse
        );
    }

    &.base-button-secondary {
        background: $c-surface-raised;

        @include flatStates(
            linear-gradient($c-state-hover, $c-state-hover) $c-surface-raised,
            linear-gradient($c-state-pressed, $c-state-pressed) $c-surface-raised
        );
    }

    &.base-button-outline {
        background: transparent;
        box-shadow: var(--shadow-outline);

        @include flatStates($c-state-hover, $c-state-pressed);

        // R1 review fix #2: focusRing's `box-shadow: var(--shadow-focus)` (on the
        // root, lower specificity) loses outright to this variant's own always-on
        // ring above (same property, higher specificity) — the keyboard focus
        // ring never showed here. Layer both shadows explicitly; the extra
        // `:focus-visible` pseudo-class gives this rule strictly higher
        // specificity than the always-on rule above, so it always wins when focused.
        &:focus-visible {
            box-shadow: var(--shadow-outline), var(--shadow-focus);
        }

        // Follow (Buttons §05): identical off/on — only the label and the
        // leading accent check change (Figma node 359:210 — the check is a
        // LEADING icon, never trailing).
        &.base-button-active .base-button-icon {
            color: $c-accent-default;
        }
    }

    &.base-button-tertiary {
        color: $c-text-secondary;
        background: transparent;

        @include flatStates($c-state-hover, $c-state-pressed);

        &:not(:disabled):hover {
            color: $c-text-primary;
        }

        // Toggle on-state (§05): glyph goes accent, container never changes.
        &.base-button-active .base-button-icon {
            color: $c-accent-default;
        }
    }

    &.base-button-destructive {
        color: $c-text-on-accent;
        background: $c-status-error;

        // R1 review fix #6: hover and press both used pressed-on-light, so the two
        // states were visually identical. Match the inverse variant's pattern —
        // hover-on-light for hover, pressed-on-light for press.
        @include flatStates(
            linear-gradient($c-state-hover-on-light, $c-state-hover-on-light) $c-status-error,
            linear-gradient($c-state-pressed-on-light, $c-state-pressed-on-light) $c-status-error
        );
    }

    // Panel-open tint — the only state where a container moves for a two-state button.
    &.base-button-selected:not(.base-button-primary):not(.base-button-inverse):not(.base-button-destructive) {
        background: $c-state-selected;
    }

    // Disabled = one neutral treatment, never an opacity dim.
    // R1 review fix #1: Vue's scoped compiler keeps a bare `&:disabled` (nothing
    // but pseudo-classes chained onto the `:where()` root) NESTED INSIDE the
    // `:where()`, so it stays specificity-neutral (0,1,0 — only `:disabled`
    // counts) and loses to every `&.base-button-<variant>` rule (0,2,0) and
    // their hover/press states and `.base-button-selected` (0,5,0 each). A native
    // `<button disabled>` always carries the `disabled` ATTRIBUTE too, and an
    // attribute selector is a real compound Vue places OUTSIDE the `:where()` —
    // repeating it is a no-op for matching (same elements match) but raises the
    // specificity to 0,6,0, clear of every competitor above.
    &[disabled][disabled][disabled][disabled][disabled] {
        color: $c-text-disabled;
        cursor: not-allowed;
        background: $c-surface-raised;
        box-shadow: none;
        transform: none;
    }

    &.base-button-loading {
        cursor: progress;
    }

    &.base-button-cannot-be-hovered {
        pointer-events: none;
        cursor: default;
    }

    // ---- DEPRECATED chips (T10 Badges replaces both) -----------------------

    &.base-button-tag {
        height: 24px;
        padding: 0 $space-12;
        font-size: 12px;
        font-weight: 700;
        color: $c-white;
        background: transparent;
        border-radius: $radius-3;
        // R1 review fix #7: without a stacking context, `::before`'s
        // `z-index: $layer-negative-z-index` escapes past THIS button and can
        // paint behind an ancestor's background instead of just this chip's fill.
        isolation: isolate;

        &::before {
            position: absolute;
            inset: 0;
            z-index: $layer-negative-z-index;
            content: '';
            background: $c-text-tertiary;
            border-radius: $radius-3;
            opacity: 0.1;

            @include transition(opacity);
        }

        &:hover::before {
            opacity: 0.4;
        }

        &:active::before,
        &.base-button-active::before {
            background: $c-accent-default;
            opacity: 1;
        }
    }

    &.base-button-tag-secondary {
        height: 24px;
        padding: 0 $space-12;
        font-size: 12px;
        font-weight: 700;
        color: $c-white;
        background: transparent;
        border-radius: $radius-3;
        box-shadow: var(--shadow-chip-outline);

        @include flatStates($c-state-hover, $c-state-pressed);

        // R1 review fix #2 (chip half): same issue as Outline above — layer the
        // keyboard focus ring on top of this chip's always-on ring.
        &:focus-visible {
            box-shadow: var(--shadow-chip-outline), var(--shadow-focus);
        }
    }
}

:where(.base-button-main) {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
}

:where(.base-button-icon) {
    flex-shrink: 0;
}
</style>
